import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Activity, Download, Trash2, Search, Calendar, User, FileText, TrendingUp } from 'lucide-react';

const API = `${getBackendUrl()}/api`;

const ACTION_COLORS = {
  login: 'bg-green-100 text-green-800',
  logout: 'bg-gray-100 text-gray-800',
  create: 'bg-blue-100 text-blue-800',
  update: 'bg-yellow-100 text-yellow-800',
  delete: 'bg-red-100 text-red-800',
  view: 'bg-purple-100 text-purple-800',
  export: 'bg-indigo-100 text-indigo-800',
  import: 'bg-cyan-100 text-cyan-800',
  approve: 'bg-teal-100 text-teal-800',
  publish: 'bg-pink-100 text-pink-800',
  download: 'bg-orange-100 text-orange-800'
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    user_id: '',
    action: '',
    resource_type: '',
    date_from: '',
    date_to: '',
    limit: 100,
    skip: 0
  });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API}/activity-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ensure logs is an array
      const logsData = Array.isArray(response.data.logs) ? response.data.logs : [];
      setLogs(logsData);
      setTotal(response.data.total || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load activity logs');
      setLogs([]); // Set to empty array on error
      setTotal(0);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activity-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('This will delete all logs older than 45 days. Continue?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/activity-logs/cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('Error cleaning up logs:', error);
      toast.error('Failed to cleanup logs');
    }
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Email', 'Role', 'Action', 'Resource Type', 'Resource Name', 'Details'];
    const rows = logs.map(log => [
      new Date(log.timestamp).toLocaleString(),
      log.user_name,
      log.user_email,
      log.user_role,
      log.action,
      log.resource_type || '-',
      log.resource_name || '-',
      log.details ? JSON.stringify(log.details) : '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Activity className="w-8 h-8 text-blue-600" />
            Activity Logs
          </h1>
          <p className="text-gray-600 mt-1">Monitor all user activities and system changes</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportToCSV} variant="outline" size="sm" disabled={logs.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={handleCleanup} variant="outline" size="sm">
            <Trash2 className="w-4 h-4 mr-2" />
            Cleanup Old Logs
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Total Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats.total_logs.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Last 7 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats.recent_logs_7days.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <User className="w-4 h-4" />
                Top Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {stats.top_users.slice(0, 3).map((user, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="truncate">{user.name || user._id}</span>
                    <span className="font-semibold">{user.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Action Type</label>
              <Select value={filters.action || 'all'} onValueChange={(value) => setFilters({...filters, action: value === 'all' ? '' : value, skip: 0})}>
                <SelectTrigger>
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="logout">Logout</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="view">View</SelectItem>
                  <SelectItem value="export">Export</SelectItem>
                  <SelectItem value="import">Import</SelectItem>
                  <SelectItem value="approve">Approve</SelectItem>
                  <SelectItem value="publish">Publish</SelectItem>
                  <SelectItem value="download">Download</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Resource Type</label>
              <Input
                placeholder="e.g., employees, batches"
                value={filters.resource_type}
                onChange={(e) => setFilters({...filters, resource_type: e.target.value, skip: 0})}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Date From</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value, skip: 0})}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Date To</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value, skip: 0})}
              />
            </div>

            <div className="flex items-end">
              <Button
                onClick={() => setFilters({
                  user_id: '',
                  action: '',
                  resource_type: '',
                  date_from: '',
                  date_to: '',
                  limit: 100,
                  skip: 0
                })}
                variant="outline"
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity History ({total} total records)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading logs...</p>
          ) : logs.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No activity logs found</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div key={log.id} className="border rounded-lg p-4 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800'}>
                          {log.action}
                        </Badge>
                        <span className="text-sm font-medium">{log.user_name}</span>
                        <span className="text-xs text-gray-500">({log.user_email})</span>
                        <Badge variant="outline" className="text-xs">
                          {log.user_role}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-gray-700">
                        {log.resource_type && (
                          <span>
                            {log.action} <strong>{log.resource_type}</strong>
                            {log.resource_name && <span> - {log.resource_name}</span>}
                          </span>
                        )}
                        {!log.resource_type && <span>User activity</span>}
                      </div>

                      {log.details && (
                        <div className="mt-2 text-xs text-gray-500 bg-gray-100 p-2 rounded">
                          {JSON.stringify(log.details)}
                        </div>
                      )}
                    </div>

                    <div className="text-right text-sm text-gray-500 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {formatDate(log.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > filters.limit && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t">
              <p className="text-sm text-gray-600">
                Showing {filters.skip + 1} to {Math.min(filters.skip + filters.limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setFilters({...filters, skip: Math.max(0, filters.skip - filters.limit)})}
                  disabled={filters.skip === 0}
                  size="sm"
                  variant="outline"
                >
                  Previous
                </Button>
                <Button
                  onClick={() => setFilters({...filters, skip: filters.skip + filters.limit})}
                  disabled={filters.skip + filters.limit >= total}
                  size="sm"
                  variant="outline"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;
